import { useEffect, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./Articles.module.css";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

const CATEGORIES = [
  ["NEWS",  "School News"],
  ["EVENT", "Event"],
];

const blank = { title: "", summary: "", body: "", category: "NEWS" };

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

export default function ArticlesPage() {
  const user = getUser();
  const role = user?.role;
  const canPost = role === "Admin" || role === "SuperAdmin";

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);

  const [editing, setEditing] = useState(null);   // "new" | article | null
  const [form, setForm]       = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState("");

  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/articles`);
      if (!res.ok) throw new Error("Failed to load");
      setItems(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setEditing("new");
    setForm({ ...blank });
    setMediaFile(null);
    setMediaPreview("");
    setError("");
  }

  function startEdit(row) {
    setEditing(row);
    setForm({
      title:    row.title    || "",
      summary:  row.summary  || "",
      body:     row.body     || "",
      category: row.category || "NEWS",
    });
    setMediaFile(null);
    setMediaPreview(row.mediaUrl || "");
    setError("");
  }

  function cancel() {
    setEditing(null);
    setForm(null);
    setMediaFile(null);
    setMediaPreview("");
    setError("");
  }

  function onPickMedia(e) {
    const f = e.target.files?.[0];
    if (!f) return;

    const isVideo = f.type.startsWith("video/");
    const isImage = f.type.startsWith("image/");
    const MB = 1024 * 1024;
    const IMAGE_MAX = 10 * MB;
    const VIDEO_MAX = 100 * MB;

    if (!isVideo && !isImage) {
      setError("Only image or video files are allowed.");
      e.target.value = "";
      return;
    }
    if (isImage && f.size > IMAGE_MAX) {
      setError(`Image is ${(f.size / MB).toFixed(1)} MB — max 10 MB. Please compress or resize before uploading.`);
      e.target.value = "";
      return;
    }
    if (isVideo && f.size > VIDEO_MAX) {
      setError(`Video is ${(f.size / MB).toFixed(1)} MB — max 100 MB. Please compress the video (try HandBrake, or export at 720p) before uploading.`);
      e.target.value = "";
      return;
    }

    setError("");
    setMediaFile(f);
    setMediaPreview(URL.createObjectURL(f));
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const isNew = editing === "new";
      const url = isNew
        ? `${API_BASE}/api/articles`
        : `${API_BASE}/api/articles/${editing.id}`;

      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      const saved = await res.json();

      if (mediaFile) {
        const fd = new FormData();
        fd.append("file", mediaFile);
        const up = await fetch(`${API_BASE}/api/articles/${saved.id}/media`, {
          method: "POST",
          body: fd,
        });
        if (!up.ok) {
          throw new Error(await extractErrorMessage(up, "Media upload failed"));
        }
      }

      await load();
      cancel();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function remove(row) {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/articles/${row.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function clearMedia(row) {
    if (!window.confirm("Remove the featured image/video?")) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/articles/${row.id}/media`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to remove media");
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <div className={s.eyebrow}>News &amp; Events</div>
          <h1 className={s.title}>Articles</h1>
          <p className={s.sub}>
            {canPost
              ? "Publish school news and events with images or videos."
              : "Stay up to date with the latest from PathWise Academy."}
          </p>
        </div>
        {canPost && !editing && (
          <button className={s.btnPrimary} onClick={startCreate}>+ New article</button>
        )}
      </div>

      {error && <div className={s.error}>{error}</div>}

      {editing && (
        <div className={s.card}>
          <h2 className={s.cardTitle}>
            {editing === "new" ? "New article" : "Edit article"}
          </h2>
          <form onSubmit={save} className={s.form}>
            <div className={s.fieldFull}>
              <label className={s.label}>Title <span className={s.req}>*</span></label>
              <input
                className={s.input}
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Annual Sports Day 2026"
                required
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Category</label>
              <select
                className={s.input}
                value={form.category}
                onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
              </select>
            </div>

            <div className={s.field}>
              <label className={s.label}>Featured image or video</label>
              <input
                className={s.fileInput}
                type="file"
                accept="image/*,video/*"
                onChange={onPickMedia}
              />
            </div>

            {mediaPreview && (
              <div className={s.fieldFull}>
                <div className={s.previewBox}>
                  {mediaFile?.type?.startsWith("video/") || (editing?.mediaType === "video" && !mediaFile)
                    ? <video src={mediaPreview} controls className={s.previewMedia} />
                    : <img src={mediaPreview} alt="preview" className={s.previewMedia} />
                  }
                </div>
              </div>
            )}

            <div className={s.fieldFull}>
              <label className={s.label}>Short summary</label>
              <input
                className={s.input}
                value={form.summary}
                onChange={e => setForm(p => ({ ...p, summary: e.target.value }))}
                placeholder="One‑line teaser shown in the article card"
                maxLength={500}
              />
            </div>

            <div className={s.fieldFull}>
              <label className={s.label}>Body <span className={s.req}>*</span></label>
              <textarea
                className={s.textarea}
                rows={10}
                value={form.body}
                onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                placeholder="Write the full article here. Line breaks are preserved."
                required
              />
            </div>

            <div className={s.formActions}>
              <button type="button" className={s.btnGhost} onClick={cancel}>Cancel</button>
              <button type="submit" className={s.btnPrimary} disabled={busy}>
                {busy ? "Saving…" : (editing === "new" ? "Publish" : "Save changes")}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && <div className={s.empty}>Loading…</div>}
      {!loading && items.length === 0 && (
        <div className={s.empty}>
          No articles yet.{canPost ? " Click “New article” to publish the first one." : ""}
        </div>
      )}

      <div className={s.grid}>
        {items.map(a => {
          const expanded = expandedId === a.id;
          return (
            <article key={a.id} className={s.articleCard}>
              {a.mediaUrl && (
                <div className={s.mediaWrap}>
                  {a.mediaType === "video"
                    ? <video src={a.mediaUrl} controls className={s.media} />
                    : <img src={a.mediaUrl} alt={a.title} className={s.media} />
                  }
                </div>
              )}
              <div className={s.cardBody}>
                <div className={s.cardMeta}>
                  <span className={`${s.badge} ${a.category === "EVENT" ? s.badgeEvent : s.badgeNews}`}>
                    {a.category === "EVENT" ? "Event" : "News"}
                  </span>
                  <span className={s.cardDate}>{fmtDate(a.postedAt)}</span>
                </div>
                <h3 className={s.articleTitle}>{a.title}</h3>
                {a.summary && <p className={s.summary}>{a.summary}</p>}

                {expanded && (
                  <div className={s.bodyText}>{a.body}</div>
                )}

                <div className={s.cardFooter}>
                  <button className={s.readMore} onClick={() => setExpandedId(expanded ? null : a.id)}>
                    {expanded ? "Show less" : "Read more →"}
                  </button>
                  {a.postedByUsername && (
                    <span className={s.byline}>by {a.postedByUsername}</span>
                  )}
                </div>

                {canPost && (
                  <div className={s.adminRow}>
                    <button className={s.btnGhost} onClick={() => startEdit(a)}>Edit</button>
                    {a.mediaUrl && (
                      <button className={s.btnGhost} onClick={() => clearMedia(a)}>Remove media</button>
                    )}
                    <button className={s.btnDanger} onClick={() => remove(a)}>Delete</button>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
